/**
 * WELCOME SIGN-UP POPUP — merchant, 2026-08-22.
 *
 * Opens once per visitor, after a delay, and never again once it has been
 * dismissed or submitted. See sections/signup-popup.liquid for why this
 * captures a real Shopify customer rather than a list somewhere else.
 *
 * IIFE-guarded on `window.__dramourSignupBound`: docs/54 §5.6 — any script a
 * section can emit twice must be, or the second copy throws at parse time on
 * the re-declared class and the guard never runs.
 */
(function () {
  if (window.__dramourSignupBound) return;
  window.__dramourSignupBound = true;

  class SignupPopup extends HTMLElement {
    connectedCallback() {
      this.key = this.dataset.key || 'dramour-signup';
      this.panel = this.querySelector('.signup-popup__panel');

      /* ---------------------------------------------------------------------
         EVERY LISTENER IS BOUND BEFORE ANY EARLY RETURN, AND THAT ORDERING IS
         THE WHOLE FIX. Merchant report, 2026-08-22: "after putting email and
         getting code the window doesn't go away even if i press x".

         The close handler and `this.escape` used to be assigned at the BOTTOM
         of this method, below three `return`s. The success branch returned
         before reaching them, so the one popup state that is guaranteed to be
         open — the one showing the customer their code — was the one state
         with no way to close it. `open()` then called
         `addEventListener('keyup', this.escape)` with `this.escape` still
         undefined, which is a silent no-op, so Escape was dead too. The X was
         not broken; it was never wired.

         The error branch had it just as badly, and worse in kind: a visitor who
         mistyped their email was shown a form they could neither fix past nor
         dismiss.

         Binding first costs nothing on the paths that return early — a
         listener on an element that stays hidden never fires — and it removes
         the whole class of bug, rather than the one state that was reported.
         --------------------------------------------------------------------- */
      this.escape = (event) => {
        if (event.key === 'Escape') this.close();
      };

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-signup-close]')) this.close();
      });

      /* Carry the typed number into a field the customer form definitely
         keeps — see the note in the section. Bound up here with the rest
         because the ERROR state re-renders the form: the visitor retypes their
         number there, and down below this listener would not exist for them. */
      this.addEventListener('input', (event) => {
        const phone = event.target.closest('[data-signup-phone]');
        if (!phone) return;
        const note = this.querySelector('[data-signup-note]');
        if (note) note.value = phone.value.trim() ? `phone: ${phone.value.trim()}` : '';
      });

      /* THE SUCCESS STATE IS A FULL PAGE LOAD, NOT AN AJAX RESPONSE.
         `{% form 'customer' %}` posts and redirects back with
         `?form_type=customer&posted=...`; Liquid then renders the success
         markup. So on the way back the popup must OPEN rather than wait for a
         delay that would never feel connected to the click. */
      if (this.querySelector('.signup-popup__success')) {
        this.seen();
        this.open();
        return;
      }

      /* An error also has to reopen — otherwise a mistyped email silently
         dismisses the popup and the visitor never gets another one. */
      if (this.querySelector('.signup-popup__error')) {
        this.open();
        return;
      }

      if (this.alreadySeen()) return;

      /* NOT ON CHECKOUT OR THE ORDER PAGES. Someone mid-purchase does not need
         a discount popup over the form they are filling in, and on the thank
         you page the offer has already been missed. */
      if (/\/(checkouts?|orders)\//.test(window.location.pathname)) return;

      this.armAfterEngagement();
    }

    /* THE DELAY STARTS WHEN THE VISITOR DOES SOMETHING — 2026-10-06.
       -----------------------------------------------------------------------
       It used to start at page load: on live the delay is 2s, so the popup
       covered the page before the visitor had scrolled once. Lighthouse then
       measured its photo as the page's main content — the largest paint on
       the collection, search and offers pages landed at 15-16s instead of the
       page's own content — and a visitor who had not yet seen a single bag
       was asked for their email.

       Now the first scroll, tap, click or key press starts the merchant's
       delay. Largest Contentful Paint stops recording at the first input, so
       the popup can no longer be the page's LCP for anyone; and the offer
       arrives once the visitor has started looking, not before. */
    armAfterEngagement() {
      const delay = Number(this.dataset.delay || 5000);
      /* The page's own scroll only — see component-product-card.js: in the
         capture phase a carousel scrolling itself reaches window too. */
      const inputs = ['pointerdown', 'keydown', 'touchstart'];
      const start = () => {
        inputs.forEach((name) => window.removeEventListener(name, start, true));
        window.removeEventListener('scroll', start);
        if (this.timer || this.hasAttribute('open')) return;
        this.timer = window.setTimeout(() => this.openWhenIdle(), delay);
        /* THE PHOTO LOADS DURING THE DELAY, NOT AFTER IT — 2026-10-24. It is
           `loading="lazy"` inside a hidden panel, so it was only requested when
           the popup opened, and on a phone the offer appeared with an empty
           cream block where the photo goes. Fetching it here is safe for the
           reason above: LCP stopped recording at the input that called this. */
        const photo = this.querySelector('.signup-popup__media img');
        if (photo) photo.loading = 'eager';
      };
      inputs.forEach((name) => window.addEventListener(name, start, { capture: true, passive: true }));
      window.addEventListener('scroll', start, { passive: true });
    }

    /* ⚠ THE TIMER USED TO FIRE OVER WHATEVER THE VISITOR WAS DOING.
       -----------------------------------------------------------------------
       Found 2026-09-29 by the axe suite, which timed out waiting for search
       results on mobile. The screenshot showed why: the visitor had opened
       search and typed "sling", and at the delay this popup opened ON TOP of
       the search overlay — and `open()` then moved focus into its email
       field, so the next keystrokes of the search went into the signup form.
       On live the delay is 2s, so anyone who heads straight for search, the
       menu or a quick view is interrupted mid-task by a discount offer.

       So the timer now only means "no sooner than". When it fires, the popup
       opens if nothing else has the visitor's attention, and otherwise looks
       again every 1.5s. Busy means any of:
         - another overlay holds the scroll lock (every modal in this theme
           sets `overflow-hidden` or `overflow-hidden-mobile` on body)
         - a visible `aria-modal` panel or open <dialog> that is not this one,
           or the add-to-cart confirmation
         - focus is in a text field — someone is typing
       The success and error paths above call `open()` directly and are not
       deferred: those are the answer to something the visitor just did. */
    openWhenIdle() {
      if (this.isBusy()) {
        this.timer = window.setTimeout(() => this.openWhenIdle(), 1500);
        return;
      }
      this.open();
    }

    isBusy() {
      const body = document.body.classList;
      if (body.contains('overflow-hidden') || body.contains('overflow-hidden-mobile')) return true;

      const active = document.activeElement;
      const typing =
        'input:not([type="hidden"]):not([type="radio"]):not([type="checkbox"]):not([type="submit"]):not([type="button"]), textarea, select, [contenteditable="true"]';
      if (active && !this.contains(active) && active.matches(typing)) return true;

      return Array.from(document.querySelectorAll('[aria-modal="true"], dialog[open], .cart-notification.active')).some(
        (el) => {
          if (this.contains(el)) return false;
          const r = el.getBoundingClientRect();
          const cs = getComputedStyle(el);
          return r.width > 0 && r.height > 0 && cs.visibility !== 'hidden' && cs.display !== 'none';
        },
      );
    }

    alreadySeen() {
      try {
        return window.localStorage.getItem(this.key) === '1';
      } catch (error) {
        /* Private mode and blocked storage throw on read. A visitor whose
           browser refuses storage should see the popup once per page rather
           than never — and never rather than every few seconds, which is why
           this returns false and the open() path still writes the flag. */
        return false;
      }
    }

    seen() {
      try {
        window.localStorage.setItem(this.key, '1');
      } catch (error) {
        /* Nothing to do. The popup simply is not suppressed next time. */
      }
    }

    open() {
      if (this.hasAttribute('open')) return;
      this.removeAttribute('hidden');
      this.setAttribute('open', '');
      this.opener = document.activeElement;
      /* Release only a lock this popup took. `close()` used to remove
         `overflow-hidden` unconditionally, so closing it over another overlay
         unlocked the page scrolling behind THAT overlay. */
      this.ownsScrollLock = !document.body.classList.contains('overflow-hidden');
      document.body.classList.add('overflow-hidden');
      /* Marks the whole document so the app launchers can stand down — see the
         `.signup-open` rule in component-overlay.css. A chat bubble on top of a
         modal is two interruptions competing, and the one the visitor did not
         ask for should lose. */
      document.body.classList.add('signup-open');
      document.addEventListener('keyup', this.escape);

      /* `trapFocus` is global.js's, already loaded on every page. The first
         field is the focus target rather than the panel, so a keyboard user
         lands on the thing they are being asked for. */
      const first = this.querySelector('input:not([type="hidden"]), .signup-popup__success, button');
      if (typeof window.trapFocus === 'function') window.trapFocus(this.panel, first);
      else if (first) first.focus();
    }

    close() {
      window.clearTimeout(this.timer);
      if (!this.hasAttribute('open')) return;
      this.removeAttribute('open');
      this.setAttribute('hidden', '');
      if (this.ownsScrollLock) document.body.classList.remove('overflow-hidden');
      document.body.classList.remove('signup-open');
      document.removeEventListener('keyup', this.escape);
      if (typeof window.removeTrapFocus === 'function') window.removeTrapFocus(this.opener);
      else if (this.opener && this.opener.focus) this.opener.focus();

      /* Dismissal counts as an answer. Asking again on the next page view is
         how a popup becomes the thing people leave a site over. */
      this.seen();
    }
  }

  if (!customElements.get('signup-popup')) customElements.define('signup-popup', SignupPopup);
})();
